import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { client, urlFor } from './SanityClient';
import './ServiceSection.css';

const FIND_SERVICE_PRIMARY = /* groq */ `
*[
  _type == "serviceItem" &&
  serviceSlug == $slug
][0]{
  _id,
  header,
  heroImage,
  description[],
  gallery[]{
    asset->{ _id, url },
    alt,
    caption
  }
}
`;

const FIND_SERVICE_FALLBACK = /* groq */ `
*[
  _type == "serviceItem" &&
  (
    serviceSlug.current == $slug ||
    lower(serviceSlug) == lower($slug) ||
    lower(serviceSlug.current) == lower($slug)
  )
][0]{
  _id,
  header,
  heroImage,
  description[],
  gallery[]{
    asset->{ _id, url },
    alt,
    caption
  }
}
`;

const RELATED_PROJECTS_BY_SERVICE_ID = /* groq */ `
*[
  _type == "project" &&
  references($serviceId) &&
  !(_id in path("drafts.**"))
]{
  _id,
  title,
  "slug": coalesce(projectSlug.current, projectSlug),
  mainImage
}[0..50]
`;

/* ---- utils ---- */
function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function ServiceSection({ slug }) {
  const [service, setService] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  // carousel + lightbox
  const [idx, setIdx] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const startX = useRef(null);

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      setService(null);
      setRelated([]);
      setIdx(0);
      setLightboxIdx(-1);

      try {
        let svc = await client.fetch(FIND_SERVICE_PRIMARY, { slug });
        if (!svc) svc = await client.fetch(FIND_SERVICE_FALLBACK, { slug });
        if (!mounted) return;

        if (!svc) {
          setLoading(false);
          return;
        }

        setService(svc);

        const projects = await client.fetch(RELATED_PROJECTS_BY_SERVICE_ID, {
          serviceId: svc._id,
        });
        if (!mounted) return;

        setRelated(shuffle(projects || []).slice(0, 3));
        setLoading(false);
      } catch (e) {
        console.error('ServiceSection fetch error:', e);
        if (mounted) setLoading(false);
      }
    })();

    return () => { mounted = false; };
  }, [slug]);

  if (loading) return null;

  if (!service) {
    return (
      <section id={slug} className="service-section" aria-labelledby={`${slug}-title`}>
        <h2 id={`${slug}-title`} className="service-title">No content for “{slug}”</h2>
        <div className="service-text">
          <p>We couldn’t find a published Service Entry matching that slug.</p>
        </div>
      </section>
    );
  }

  // Build normalized gallery: hero first + gallery[], de-duped
  const gallery = (() => {
    const out = [];
    if (service.heroImage) {
      out.push({
        src: urlFor(service.heroImage).width(1600).auto('format').url(),
        alt: service.heroImage?.alt || service.header || slug,
        caption: '',
      });
    }
    if (Array.isArray(service.gallery)) {
      for (let i = 0; i < service.gallery.length; i++) {
        const g = service.gallery[i];
        if (!g) continue;
        const src = g.asset?.url || urlFor(g).width(1600).auto('format').url();
        if (src) {
          out.push({
            src,
            alt: g.alt || `${service.header || slug} image ${i + 1}`,
            caption: g.caption || '',
          });
        }
      }
    }
    const seen = new Set();
    return out.filter(img => (seen.has(img.src) ? false : (seen.add(img.src), true)));
  })();

  const count = gallery.length;
  const go   = (n) => setIdx((n + count) % Math.max(count, 1));
  const next = () => go(idx + 1);
  const prev = () => go(idx - 1);

  const onTouchStart = (e) => (startX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (startX.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    startX.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) next();
    else prev();
  };

  // Extract plain text paragraphs from portable text blocks
  const paragraphs = Array.isArray(service.description)
    ? service.description
        .filter(b => b?._type === 'block' && Array.isArray(b.children))
        .map(b => (b.children || []).map(c => c?.text || '').join(' ').trim())
        .filter(t => t.length > 0)
    : [];

  return (
    <section id={slug} className="service-section" aria-labelledby={`${slug}-title`}>
      <h2 id={`${slug}-title`} className="service-title">
        {service.header || slug}
      </h2>

      {/* Left: text  |  Right: carousel */}
      <div className="service-content ss-content">
        <div className="service-text ss-text">
          {paragraphs.map((text, i) => (
            <p key={i} className={i === 0 ? 'lead' : undefined}>
              {text}
            </p>
          ))}
        </div>

        <div className="service-media ss-media">
          {count > 0 ? (
            <div
              className="ss-carousel"
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
              aria-label="Service image carousel"
            >
              <div
                className="ss-carousel__viewport"
                onClick={() => setLightboxIdx(idx)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setLightboxIdx(idx)}
              >
                <img
                  key={gallery[idx].src}
                  src={gallery[idx].src}
                  alt={gallery[idx].alt}
                  className="ss-carousel__img"
                  loading="lazy"
                />
              </div>

              {/* Optional caption */}
              {gallery[idx].caption ? (
                <div className="ss-carousel__caption">{gallery[idx].caption}</div>
              ) : null}

              {count > 1 && (
                <>
                  <button
                    className="ss-carousel__nav ss-carousel__nav--left"
                    onClick={prev}
                    aria-label="Previous image"
                    type="button"
                  >
                    ‹
                  </button>
                  <button
                    className="ss-carousel__nav ss-carousel__nav--right"
                    onClick={next}
                    aria-label="Next image"
                    type="button"
                  >
                    ›
                  </button>

                  <div className="ss-carousel__dots" role="tablist" aria-label="Image selector">
                    {gallery.map((g, i) => (
                      <button
                        key={g.src}
                        role="tab"
                        aria-selected={i === idx}
                        aria-label={`Image ${i + 1}`}
                        className={`ss-dot ${i === idx ? 'is-active' : ''}`}
                        onClick={() => setIdx(i)}
                        type="button"
                        title={`Image ${i + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="ss-carousel__empty">No images available</div>
          )}
        </div>
      </div>

      {/* LIGHTBOX */}
      {lightboxIdx >= 0 && (
        <div
          className="ss-lightbox"
          role="dialog"
          aria-label="Image viewer"
          onClick={() => setLightboxIdx(-1)}
        >
          <img
            src={gallery[lightboxIdx].src}
            alt={gallery[lightboxIdx].alt}
            className="ss-lightbox__img"
          />
        </div>
      )}

      {/* RELATED PROJECTS */}
      {related.length > 0 && (
        <div className="related-projects ss-related">
          <h3 className="related-projects__title">Related Projects</h3>
          <ul className="ss-related__grid">
            {related.map((p) => {
              const imgUrl = p.mainImage
                ? urlFor(p.mainImage).width(800).height(500).fit('crop').auto('format').url()
                : null;

              const href = p.slug ? `/our-work/${p.slug}` : `/our-work/id/${p._id}`;

              return (
                <li key={p._id} className="ss-related__card">
                  <Link to={href} className="ss-related__link">
                    {imgUrl && (
                      <img
                        src={imgUrl}
                        alt={p.title}
                        loading="lazy"
                        className="ss-related__img"
                      />
                    )}
                    <div className="ss-related__body">
                      <h4 className="ss-related__title">{p.title}</h4>
                      <span className="ss-related__cta">View project →</span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}
